/**
 * One dish, on its own: the photograph, and under it what goes into it.
 *
 * This replaces two screens that disagreed. The table view opened a list of
 * ingredient names with no photograph and read only one of the four ways an
 * ingredient reaches a dish; the station view opened a different modal with the
 * variant and the notes and no ingredients at all. A cook got a different
 * answer depending on which view they happened to be looking at.
 *
 * Two things this must never do. It must never show an empty ingredient list —
 * that reads as "there is nothing in it", and for someone avoiding something
 * that is the most dangerous answer available; when nothing is recorded it says
 * so in words. And it must never let a tall photograph push the ingredients or
 * the close button off the screen, which is why the image is height-bounded and
 * the close button lives in the frame.
 *
 * One exception to the first rule: an off-menu plate (feature 248) has no
 * recipe by construction — the waiter's note IS the recipe — so the whole
 * ingredients section is left out rather than announcing an absence that will
 * always be there. Its title is the waiter's text, never the anchor dish's.
 */
import { useState } from "react";
import { Trans, useTranslation } from "../i18n";
import { buildDishIngredients } from "../lib/dish-ingredients";
import { isOffMenuItem, itemDisplayName, OFF_MENU_TAG } from "../lib/off-menu";
import { STATUS_LABEL } from "../lib/order-card";
import ModalShell from "./ModalShell";
import ScrollPane from "./ScrollPane";

interface DishDetailModalProps {
  /** The order line that was pressed. */
  item: any;
  onClose: () => void;
}

export default function DishDetailModal({ item, onClose }: DishDetailModalProps) {
  const { t } = useTranslation();
  const [photoFailed, setPhotoFailed] = useState(false);

  const dishName: string = itemDisplayName(item, t("kds.dish.fallbackName", "Piatto"));
  const offMenu: boolean = isOffMenuItem(item);
  const photo: string | null = item?.dish?.photo ?? null;
  const description: string | null = item?.dish?.description ?? null;
  const ingredients = buildDishIngredients(item);

  const quantity = Number(item?.quantity) || 1;
  const status = STATUS_LABEL[item?.status]?.(t) ?? item?.status ?? "—";
  const variant: string | undefined = item?.variant?.name ?? item?.variantName;

  return (
    <ModalShell
      title={dishName}
      subtitle={
        <span>
          {offMenu && (
            <span className="uppercase tracking-wider text-kds-muted bg-kds-muted/15 px-2 py-0.5 rounded mr-3">
              {OFF_MENU_TAG(t)}
            </span>
          )}
          <Trans
            i18nKey="kds.dish.quantityStatus"
            defaults="Quantità <b>{{quantity}}</b> · Stato <b>{{status}}</b>"
            values={{ quantity, status }}
            components={{ b: <b className="text-kds-text" /> }}
          />
        </span>
      }
      onClose={onClose}
    >
      <ScrollPane size="roomy" bodyClassName="px-8 py-6 space-y-8" labels={{ up: t("kds.scroll.up", "Sopra"), down: t("kds.scroll.down", "Sotto") }}>
        {/* The photograph. Bounded height on purpose: a portrait shot from a
            phone would otherwise fill the screen and bury the ingredients. */}
        {photo && !photoFailed && (
          <img
            src={photo}
            alt={dishName}
            onError={() => setPhotoFailed(true)}
            className="w-full max-h-[45vh] object-contain rounded-xl bg-kds-bg"
          />
        )}

        {description && (
          <p className="text-kds-note text-kds-muted leading-snug">{description}</p>
        )}

        {/* What the kitchen was told to change about this particular plate. It
            outranks the recipe: the recipe is what the dish normally is. */}
        {(variant || item?.isGlutenFreeVersion || item?.modifications || item?.notes) && (
          <div className="space-y-3 rounded-xl border-2 border-kds-preparing/50 bg-kds-preparing/10 px-6 py-5">
            {item?.isGlutenFreeVersion && (
              <p className="text-kds-dish text-kds-preparing font-bold">
                {t("kds.dish.glutenFree", "⚠ SENZA GLUTINE")}
              </p>
            )}
            {variant && (
              <p className="text-kds-note text-kds-text">
                <span className="text-kds-muted">{t("kds.dish.variantLabel", "Variante: ")}</span>
                {variant}
              </p>
            )}
            {item?.modifications && (
              <p className="text-kds-note text-kds-preparing font-bold">
                {"⚠ "}
                {item.modifications}
              </p>
            )}
            {item?.notes && <p className="text-kds-note text-kds-text">{item.notes}</p>}
          </div>
        )}

        {/* Nothing to say about the ingredients of a plate that has no recipe:
            the note above is the recipe. Announcing "no ingredients recorded"
            on every off-menu plate would be noise the cook learns to ignore. */}
        {!offMenu && (
        <div>
          <h3 className="text-kds-badge uppercase tracking-widest text-kds-muted mb-4">
            {t("kds.dish.ingredients", "Ingredienti")}
          </h3>

          {ingredients.kind === "nothing-recorded" ? (
            // In words, never an empty list.
            <p className="text-kds-note text-kds-muted italic">
              {t("kds.dish.noIngredients", "Per questo piatto non è stato registrato nessun ingrediente.")}
            </p>
          ) : (
            <ul className="space-y-3">
              {ingredients.lines.map((line, index) => (
                <li
                  key={`${line.name}-${index}`}
                  className="flex items-baseline justify-between gap-6 border-b border-kds-muted/15 pb-3"
                >
                  <span className="text-kds-dish text-kds-text">{line.name}</span>
                  {line.dose && (
                    <span className="text-kds-note text-kds-muted tabular-nums whitespace-nowrap">
                      {line.dose}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        )}
      </ScrollPane>
    </ModalShell>
  );
}
